import React, { useState, useMemo } from "react";
import { List, LayoutGrid } from "lucide-react";
import { BenchmarkFooter } from "./benchmark-footer";
import svgPaths from "../../imports/svg-97us0gosgz";


/* ── Types ─────────────────────────────────────── */

type CompetitionStatus = "ACTIVE" | "UPCOMING" | "CLOSED";
type FilterTab = "active" | "closed" | "all";

interface Competition {
  id: string;
  title: string;
  description: string;
  status: CompetitionStatus;
  startDate: string;
  deadline: string;
  participants: number;
}

/* ── Mock Data ─────────────────────────────────── */

const COMPETITIONS: Competition[] = [
  { id: "eq-theories-1", title: "Equational Theories Challenge", description: "This competition explores in-context mathematical distillation: participants submit compact, human-readabl...", status: "ACTIVE", startDate: "March 10, 2026", deadline: "April 20, 2026", participants: 2684 },
  { id: "eq-theories-2", title: "Mathematics Distillation Challenge", description: "Develop universal cheat sheets to boost LLM reasoning across the full equation-implication bank...", status: "ACTIVE", startDate: "March 10, 2026", deadline: "April 20, 2026", participants: 1832 },
  { id: "eq-theories-3", title: "Symbolic Reasoning Open", description: "Test the limits of automated theorem proving with novel approaches to symbolic mathematics...", status: "ACTIVE", startDate: "March 10, 2026", deadline: "April 20, 2026", participants: 956 },
  { id: "eq-theories-4", title: "Magma Theory Sprint", description: "A time-limited challenge focused on magma equation implications with strict resource constraints...", status: "UPCOMING", startDate: "April 1, 2026", deadline: "May 15, 2026", participants: 412 },
  { id: "eq-theories-5", title: "Proof Verification Challenge", description: "Automated verification of mathematical proofs using novel AI techniques and formal methods...", status: "ACTIVE", startDate: "March 10, 2026", deadline: "April 20, 2026", participants: 1547 },
  { id: "eq-theories-6", title: "Algebraic Structure Discovery", description: "Discover novel algebraic structures through computational exploration and machine learning...", status: "ACTIVE", startDate: "March 10, 2026", deadline: "April 20, 2026", participants: 723 },
  { id: "eq-theories-7", title: "Equation Solver Championship", description: "Compete to build the most efficient equation solving systems across diverse mathematical domains...", status: "CLOSED", startDate: "Jan 5, 2026", deadline: "Feb 20, 2026", participants: 3891 },
  { id: "eq-theories-8", title: "Abstract Algebra Open", description: "Explore the frontiers of abstract algebra with automated reasoning and proof generation...", status: "CLOSED", startDate: "Dec 1, 2025", deadline: "Jan 15, 2026", participants: 2105 },
];

/* ── Status Badge ─────────────────────────────── */

function StatusBadge({ status }: { status: CompetitionStatus }) {
  const styles: Record<CompetitionStatus, { bg: string; border: string; text: string }> = {
    ACTIVE: { bg: "#d8f0e3", border: "rgba(30,93,58,0.2)", text: "#1e5d3a" },
    UPCOMING: { bg: "#fff4e5", border: "rgba(185,80,0,0.2)", text: "#b95000" },
    CLOSED: { bg: "rgba(59,14,39,0.06)", border: "rgba(59,14,39,0.1)", text: "rgba(59,14,39,0.5)" },
  };
  const s = styles[status];
  return (
    <span
      className="inline-block px-[8px] py-[2px] font-['Helvetica_Neue:Bold',sans-serif] text-[10px] tracking-[0.6px] uppercase whitespace-nowrap"
      style={{ backgroundColor: s.bg, border: `1px solid ${s.border}`, color: s.text }}
    >
      {status}
    </span>
  );
}

/* ── Hero Illustration (Math Symbols V-Shape) ──── */

function HeroIllustration() {
  return (
    <div className="relative w-full h-full flex items-center justify-center">
      <svg viewBox="0 0 480 437" className="w-full h-full max-w-[480px]" fill="none" preserveAspectRatio="xMidYMid meet">
        <g opacity="0.9">
          {/* V-shape gradient fill */}
          <defs>
            <linearGradient id="heroVGrad" x1="240" y1="79" x2="240" y2="407" gradientUnits="userSpaceOnUse">
              <stop stopColor="var(--brand-logo-start, #340825)" />
              <stop offset="1" stopColor="var(--brand-logo-end, #AB704B)" />
            </linearGradient>
            <mask id="heroMask">
              <path d={svgPaths.p3bde4400} fill="white" />
            </mask>
          </defs>
          <g mask="url(#heroMask)">
            <path d={svgPaths.p6bb7200} fill="url(#heroVGrad)" />
          </g>
          {/* Math symbols */}
          <text x="280" y="60" fill="var(--brand-text, #3b0e27)" opacity="0.3" fontSize="32" fontFamily="serif">{"\u2260"}</text>
          <text x="360" y="80" fill="var(--brand-text, #3b0e27)" opacity="0.25" fontSize="28" fontFamily="serif">{"\u00F7"}</text>
          <text x="420" y="60" fill="var(--brand-text, #3b0e27)" opacity="0.2" fontSize="28" fontFamily="serif">{"\u221E"}</text>
          <text x="120" y="150" fill="var(--brand-text, #3b0e27)" opacity="0.2" fontSize="28" fontFamily="serif">{"\u2200"}</text>
          <text x="400" y="170" fill="var(--brand-text, #3b0e27)" opacity="0.25" fontSize="28" fontFamily="serif">{"\u03C0"}</text>
          <text x="300" y="220" fill="var(--brand-text, #3b0e27)" opacity="0.2" fontSize="28" fontFamily="serif">{"\u220F"}</text>
          <text x="130" y="260" fill="var(--brand-text, #3b0e27)" opacity="0.2" fontSize="28" fontFamily="serif">{"\u03B4"}</text>
          <text x="450" y="260" fill="var(--brand-text, #3b0e27)" opacity="0.2" fontSize="28" fontFamily="serif">{"\u221A"}</text>
          <text x="260" y="310" fill="var(--brand-text, #3b0e27)" opacity="0.2" fontSize="28" fontFamily="serif">{"\u03A3"}</text>
          <text x="220" y="380" fill="var(--brand-text, #3b0e27)" opacity="0.15" fontSize="24" fontFamily="serif">{"\u2212"}</text>
        </g>
      </svg>
    </div>
  );
}

/* ── Main Component ─────────────────────────────── */

export default function Competitions({ onCompetitionClick, onPlaygroundClick }: { onCompetitionClick?: (id: string) => void; onPlaygroundClick?: () => void }) {
  const [filter, setFilter] = useState<FilterTab>("active");
  const [viewMode, setViewMode] = useState<"list" | "grid">("list");


  const counts = useMemo(() => ({
    active: COMPETITIONS.filter(c => c.status === "ACTIVE").length,
    closed: COMPETITIONS.filter(c => c.status === "CLOSED").length,
    upcoming: COMPETITIONS.filter(c => c.status === "UPCOMING").length,
    all: COMPETITIONS.length,
  }), []);

  const filtered = useMemo(() => {
    if (filter === "active") return COMPETITIONS.filter(c => c.status === "ACTIVE" || c.status === "UPCOMING");
    if (filter === "closed") return COMPETITIONS.filter(c => c.status === "CLOSED");
    return COMPETITIONS;
  }, [filter]);

  return (
    <div className="px-[16px] md:px-[24px] pt-[24px] md:pt-[40px] pb-0 max-w-[1440px] mx-auto">
      {/* Hero Section */}
      <div className="flex flex-col lg:flex-row gap-[24px] md:gap-[32px] lg:gap-[48px] items-start mb-[40px] md:mb-[60px]">
        {/* Left: Title + Description */}
        <div className="flex-1 min-w-0 w-full lg:w-auto mx-[0px] mt-[120px] mb-[80px]">
          <p className="font-['Helvetica_Neue:Regular',sans-serif] text-[12px] tracking-[2px] uppercase opacity-40 mb-[12px]" style={{ color: 'var(--brand-text)' }}>
            Competition Catalog
          </p>
          <h1 className="font-['Helvetica_Neue:Condensed_Bold',sans-serif] m-0 leading-[1.1] text-[40px] md:text-[56px] lg:text-[72px] tracking-[-1.8px]" style={{ color: 'var(--brand-text)' }}>
            Competitions
          </h1>
          <p className="font-['Helvetica_Neue:Regular',sans-serif] opacity-50 mt-[16px] leading-[1.65] mb-[32px] text-[14px] md:text-[16px] max-w-[480px]" style={{ color: 'var(--brand-text)' }}>
            Push automated reasoning to its limits. Tackle hard equation-solving benchmarks, discover elegant proofs, and advance the frontier of symbolic intelligence.
          </p>
          <div className="flex flex-col md:flex-row gap-[16px] items-stretch md:items-center">
            <button
              onClick={() => {
                const el = document.getElementById("competitions-list");
                el?.scrollIntoView({ behavior: "smooth", block: "start" });
              }}
              className="h-[48px] shrink-0 w-full md:w-[180px] cursor-pointer border-none"
              style={{ backgroundColor: 'var(--brand-primary-btn-bg)' }}
            >
              <p className="font-['Helvetica_Neue:Medium',sans-serif] text-[16px] text-center m-0" style={{ color: 'var(--brand-primary-btn-text)' }}>Join Competitions</p>
            </button>
            <button
              onClick={onPlaygroundClick}
              className="h-[48px] shrink-0 w-full md:w-[180px] cursor-pointer bg-transparent"
              style={{ border: '1px solid var(--brand-border-strong)' }}
            >
              <p className="font-['Helvetica_Neue:Medium',sans-serif] text-[16px] text-center m-0" style={{ color: 'var(--brand-text)' }}>Playground</p>
            </button>
          </div>
        </div>
        {/* Right: Illustration */}
        <div className="flex-1 min-w-0 w-full lg:w-auto hidden md:block">
          <div className="w-full max-w-[500px] aspect-square mx-auto lg:mx-0 lg:ml-auto">
            <HeroIllustration />
          </div>
        </div>
      </div>

      {/* Competitions List */}
      <div id="competitions-list" className="scroll-mt-[100px]">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-[16px] mb-[20px]">
          <div className="flex items-center gap-[16px]">
            <h2 className="font-['Helvetica_Neue:Condensed_Bold',sans-serif] text-[24px] m-0 whitespace-nowrap" style={{ color: 'var(--brand-text)' }}>
              All Competitions
            </h2>
            <div className="h-[24px] w-px" style={{ backgroundColor: 'var(--brand-border)' }} />
            <span className="font-['Helvetica_Neue:Medium',sans-serif] text-[16px] whitespace-nowrap" style={{ color: 'var(--brand-text)' }}>
              {filtered.length} Shown
            </span>
          </div>
          <div className="flex items-center gap-[16px] md:gap-[24px]">
            {/* Filter tabs */}
            <div className="flex" style={{ border: '1px solid var(--brand-border)' }}>
              {(["active", "closed", "all"] as FilterTab[]).map(t => (
                <button
                  key={t}
                  onClick={() => setFilter(t)}
                  className="border-none cursor-pointer px-[12px] md:px-[16px] py-[6px] font-['Helvetica_Neue:Medium',sans-serif] text-[14px] tracking-[0.6px] transition-colors whitespace-nowrap"
                  style={{
                    backgroundColor: filter === t ? 'var(--brand-primary-btn-bg)' : 'transparent',
                    color: filter === t ? 'var(--brand-primary-btn-text)' : 'var(--brand-text-opacity-50)',
                  }}
                >
                  {t === "active" ? `Active ${counts.active + counts.upcoming}` : t === "closed" ? `Closed ${counts.closed}` : `All ${counts.all}`}
                </button>
              ))}
            </div>
            {/* View mode toggle */}
            <div className="hidden md:flex" style={{ border: '1px solid var(--brand-border)' }}>
              <button
                onClick={() => setViewMode("list")}
                className="border-none cursor-pointer p-[6px] transition-colors"
                style={{ backgroundColor: viewMode === "list" ? 'var(--brand-primary-btn-bg)' : 'transparent' }}
              >
                <List size={16} style={{ color: viewMode === "list" ? 'var(--brand-primary-btn-text)' : 'var(--brand-text)', opacity: viewMode === "list" ? 1 : 0.4 }} />
              </button>
              <button
                onClick={() => setViewMode("grid")}
                className="border-none cursor-pointer p-[6px] transition-colors"
                style={{ backgroundColor: viewMode === "grid" ? 'var(--brand-primary-btn-bg)' : 'transparent' }}
              >
                <LayoutGrid size={16} style={{ color: viewMode === "grid" ? 'var(--brand-primary-btn-text)' : 'var(--brand-text)', opacity: viewMode === "grid" ? 1 : 0.4 }} />
              </button>
            </div>
          </div>
        </div>

        {/* Table / Grid */}
        {viewMode === "list" ? (
          <div style={{ borderTop: '1px solid var(--brand-border)' }}>
            {/* Desktop table header */}
            <div className="hidden lg:grid" style={{ gridTemplateColumns: '48px minmax(0, 440px) auto auto auto auto auto', columnGap: '48px', borderBottom: '1px solid var(--brand-border-faint)' }}>
              <div className="px-[8px] py-[16px]">
                <span className="font-['Helvetica_Neue:Bold',sans-serif] text-[10px] tracking-[1px] uppercase opacity-30" style={{ color: 'var(--brand-text)' }}>#</span>
              </div>
              <div className="py-[16px]"><span className="font-['Helvetica_Neue:Regular',sans-serif] text-[14px] opacity-30" style={{ color: 'var(--brand-text)' }}>Competition</span></div>
              <div className="py-[16px]"><span className="font-['Helvetica_Neue:Regular',sans-serif] text-[14px] opacity-30" style={{ color: 'var(--brand-text)' }}>Status</span></div>
              <div className="py-[16px]"><span className="font-['Helvetica_Neue:Regular',sans-serif] text-[14px] opacity-30" style={{ color: 'var(--brand-text)' }}>Start Date</span></div>
              <div className="py-[16px]"><span className="font-['Helvetica_Neue:Regular',sans-serif] text-[14px] opacity-30" style={{ color: 'var(--brand-text)' }}>Submission Deadline</span></div>
              <div className="py-[16px] flex justify-end"><span className="font-['Helvetica_Neue:Regular',sans-serif] text-[14px] opacity-30" style={{ color: 'var(--brand-text)' }}>Participants</span></div>
              <div className="py-[16px] flex justify-end"><span className="font-['Helvetica_Neue:Regular',sans-serif] text-[14px] opacity-30" style={{ color: 'var(--brand-text)' }}>Action</span></div>
            </div>
            {/* Rows */}
            {filtered.map((comp, i) => (
              <div
                key={comp.id}
                className="cursor-pointer transition-colors hover:opacity-90"
                style={{ borderBottom: '1px solid var(--brand-border-faint)' }}
                onClick={() => onCompetitionClick?.(comp.id)}
              >
                {/* Desktop row */}
                <div className="hidden lg:grid items-center py-[24px]" style={{ gridTemplateColumns: '48px minmax(0, 440px) auto auto auto auto auto', columnGap: '48px' }}>
                  <div className="px-[8px]">
                    <span className="font-['Helvetica_Neue:Regular',sans-serif] text-[12px] opacity-40" style={{ color: 'var(--brand-text)' }}>
                      {String(i + 1).padStart(2, '0')}
                    </span>
                  </div>
                  <div className="pr-[16px]">
                    <p className="font-['Helvetica_Neue:Bold',sans-serif] text-[20px] tracking-[-0.4px] m-0 mb-[6px]" style={{ color: 'var(--brand-text)' }}>{comp.title}</p>
                    <p className="font-['Helvetica_Neue:Regular',sans-serif] text-[14px] opacity-60 m-0 leading-[1.6] w-full" style={{ color: 'var(--brand-text)' }}>{comp.description}</p>
                  </div>
                  <div><StatusBadge status={comp.status} /></div>
                  <div><span className="font-['Helvetica_Neue:Regular',sans-serif] text-[14px]" style={{ color: 'var(--brand-text)' }}>{comp.startDate}</span></div>
                  <div><span className="font-['Helvetica_Neue:Regular',sans-serif] text-[14px]" style={{ color: 'var(--brand-text)' }}>{comp.deadline}</span></div>
                  <div className="flex justify-end"><span className="font-['Helvetica_Neue:Regular',sans-serif] text-[14px]" style={{ color: 'var(--brand-text)' }}>{comp.participants.toLocaleString()}</span></div>
                  <div>
                    <button
                      className="border-none cursor-pointer px-[16px] py-[8px] font-['Helvetica_Neue:Medium',sans-serif] text-[14px]"
                      style={{ backgroundColor: 'var(--brand-primary-btn-bg)', color: 'var(--brand-primary-btn-text)' }}
                      onClick={(e) => { e.stopPropagation(); onCompetitionClick?.(comp.id); }}
                    >
                      Join
                    </button>
                  </div>
                </div>
                {/* Mobile/tablet card */}
                <div className="lg:hidden py-[20px] px-[4px]">
                  <div className="flex items-start justify-between gap-[12px] mb-[8px]">
                    <div className="flex-1 min-w-0">
                      <p className="font-['Helvetica_Neue:Bold',sans-serif] text-[18px] tracking-[-0.3px] m-0 mb-[4px]" style={{ color: 'var(--brand-text)' }}>{comp.title}</p>
                      <p className="font-['Helvetica_Neue:Regular',sans-serif] text-[13px] opacity-60 m-0 leading-[1.5]" style={{ color: 'var(--brand-text)' }}>{comp.description}</p>
                    </div>
                    <StatusBadge status={comp.status} />
                  </div>
                  <div className="flex flex-wrap items-center gap-[16px] mt-[12px]">
                    <span className="font-['Helvetica_Neue:Regular',sans-serif] text-[13px] opacity-50" style={{ color: 'var(--brand-text)' }}>Start: {comp.startDate}</span>
                    <span className="font-['Helvetica_Neue:Regular',sans-serif] text-[13px] opacity-50" style={{ color: 'var(--brand-text)' }}>Deadline: {comp.deadline}</span>
                    <span className="font-['Helvetica_Neue:Regular',sans-serif] text-[13px] opacity-50" style={{ color: 'var(--brand-text)' }}>{comp.participants.toLocaleString()} participants</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          /* Grid view */
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-[20px]" style={{ borderTop: '1px solid var(--brand-border)', paddingTop: '20px' }}>
            {filtered.map((comp) => (
              <div
                key={comp.id}
                className="cursor-pointer p-[24px] transition-colors"
                style={{ border: '1px solid var(--brand-border)', backgroundColor: 'var(--brand-surface)' }}
                onClick={() => onCompetitionClick?.(comp.id)}
              >
                <div className="flex items-start justify-between gap-[12px] mb-[12px]">
                  <h3 className="font-['Helvetica_Neue:Bold',sans-serif] text-[18px] tracking-[-0.3px] m-0" style={{ color: 'var(--brand-text)' }}>{comp.title}</h3>
                  <StatusBadge status={comp.status} />
                </div>
                <p className="font-['Helvetica_Neue:Regular',sans-serif] text-[13px] opacity-60 m-0 leading-[1.6] mb-[20px]" style={{ color: 'var(--brand-text)' }}>{comp.description}</p>
                <div className="flex items-center justify-between" style={{ borderTop: '1px solid var(--brand-border-faint)', paddingTop: '16px' }}>
                  <span className="font-['Helvetica_Neue:Regular',sans-serif] text-[13px] opacity-50" style={{ color: 'var(--brand-text)' }}>{comp.participants.toLocaleString()} participants</span>
                  <span className="font-['Helvetica_Neue:Regular',sans-serif] text-[13px] opacity-50" style={{ color: 'var(--brand-text)' }}>Deadline: {comp.deadline}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="mt-[60px] md:mt-[120px]">
        <BenchmarkFooter />
      </div>


    </div>
  );
}

export { COMPETITIONS };
export type { Competition };